import fetchRetry from "fetch-retry";

export interface CustomFetchOptions {
  cacheEnabled?: boolean;
  retryEnabled: boolean;
  maxRetries?: number;
  retryDelay?: number;
  cacheTTL?: number;
}

const pendingResponsesMap = new Map<string, Promise<Response>>();

export function createCustomFetch(options: CustomFetchOptions): typeof fetch {
  const {
    cacheEnabled = true,
    retryEnabled,
    maxRetries = 3,
    retryDelay = 1000,
    cacheTTL = 60000,
  } = options;

  const fetchWithRetry = fetchRetry(fetch, {
    retryDelay: (attempt) => {
      return retryDelay * Math.pow(2, attempt); // 指数バックオフ
    },
    retryOn: (attempt, error, response) => {
      // リトライが有効で、かつエラーまたは!response.okの場合
      if (!retryEnabled) return false;
      if (attempt >= maxRetries) return false;
      if (error) return true;
      if (
        response &&
        [
          408, // Request Timeout
          429, // Too Many Requests
          502, // Bad Gateway
          503, // Service Unavailable
          504, // Gateway Timeout
        ].includes(response.status)
      ) {
        return true;
      }
      return false;
    },
  });

  const customFetch: typeof fetch = async (
    input: RequestInfo | URL,
    init?: RequestInit,
  ) => {
    const url = parseURLfromRequest(input);

    if (cacheEnabled && url.pathname.startsWith("/rest/v1/")) {
      if (init?.method === "GET" || init?.method === "HEAD") {
        // Cache GET requests
        const cacheKey = url.href;
        if (pendingResponsesMap.has(cacheKey)) {
          // リクエストがすでに進行中または完了済みの場合、既存のPromiseを返す
          // ReadableStreamを複数回読み取ることはできないため、cloneして返す
          return pendingResponsesMap
            .get(cacheKey)!
            .then((response) => response.clone());
        }

        const requestPromise = fetchWithRetry(input, init)
          .then((response) => {
            if (!response.ok) {
              // Remove from cache if the response is not successful
              pendingResponsesMap.delete(cacheKey);
            }
            return response;
          })
          .catch((error) => {
            // Remove from cache if the request fails
            pendingResponsesMap.delete(cacheKey);
            throw error;
          });

        pendingResponsesMap.set(cacheKey, requestPromise);

        setTimeout(() => {
          pendingResponsesMap.delete(cacheKey);
        }, cacheTTL);

        // ReadableStreamを複数回読み取ることはできないため、cloneして返す
        return requestPromise.then((response) => response.clone());
      }

      // 非GETリクエストは安全側に倒して全てキャッシュクリア
      pendingResponsesMap.clear();
    }

    if (url.pathname.startsWith("/functions/v1/")) {
      // Function コールでデータが更新される可能性があるため、キャッシュをクリア
      pendingResponsesMap.clear();
    }

    if (
      url.pathname.startsWith("/auth/v1/") &&
      url.pathname !== "/auth/v1/user"
    ) {
      // ユーザー認証関連のリクエストは、セッション情報が変わる可能性があるため、キャッシュをクリア
      pendingResponsesMap.clear();
    }

    // Fallback with retry
    return fetchWithRetry(input, init);
  };

  return customFetch;
}

function parseURLfromRequest(request: RequestInfo | URL): URL {
  if (request instanceof Request) {
    return new URL(request.url);
  } else if (typeof request === "string") {
    return new URL(request);
  } else if (request instanceof URL) {
    return request;
  } else {
    const _exhaustiveCheck: never = request;
    return _exhaustiveCheck;
  }
}
